import {
  WSActions,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayoutSettingsResponse,
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { useWSMutation } from "@ws-react-query";
import React, { useEffect, useMemo, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import {
  PATH_SETTINGS_PAYMENT_METHODS,
  PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD
} from "..";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { WSQueries } from "../../../../../query/WSQuery";
import { useQueryInternalAccounts } from "../../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryPaymentsCardsDebit } from "../../../../../query/paymentCards/queries/useQueryPaymentsCardsDebit";
import { QUERY_PAYOUT_SETTINGS } from "../../../../../query/payments/keys";
import {
  usePayoutSettings,
  usePayoutSettingsDebitCards
} from "../../../../../query/payments/queries";
import {
  useAccounts,
  useMemberProfile
} from "../../../../../query/users/queries";
import { paymentsService } from "../../../../../services/payments";
import { LayoutFullscreen } from "../../../../../shared/components/LayoutFullscreen";
import { selectorAccountIPReady } from "../../../../../shared/selectors/selectorAccountIPReady";
import { selectorActiveBankingAccount } from "../../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorPaymentCardPayoutReady } from "../../../../../shared/selectors/selectorPaymentCardPayoutReady";
import { selectorUseInternationalInstantPayouts } from "../../../../../shared/selectors/selectorUseInternationalInstantPayouts";
import { selectorUseInternationalPayouts } from "../../../../../shared/selectors/selectorUseInternationalPayouts";
import { WSServiceError } from "../../../../../utils/serviceHelper";
import { selectorIsPlatformBalanceAccount } from "../../../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { List } from "../../../components/ManageMethods/List";
import { MethodValue } from "../../../components/ManageMethods/types";
import { useModalConfirmPayoutMethodChange } from "../../../components/ModalConfirmPayoutMethodChange";
import { useDefaultPayoutMethod } from "../../../components/payout/DefaultPayoutMethod";
import { useModalInfo } from "../../../components/payout/ModalInfo";
import { useInstantPayoutFee } from "../../../hooks/useInstantPayoutFee";
import { selectorAccountPayoutReady } from "../../../selectors/selectorAccountPayoutReady";

export const RouteManagePayoutMethod: React.FC<RouteComponentProps> = () => {
  const userId = useUserId();
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryAccounts = useAccounts();
  const queryPayoutSettingsDebitCards = usePayoutSettingsDebitCards(userId);
  const queryPaymentCards = useQueryPaymentsCardsDebit();
  const queryMember = useMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();
  const queryPayoutSettings = usePayoutSettings(userId);
  const history = useHistory();
  const queryFeatureFlags = useFeatureFlags();
  const location = useLocation<{ error?: WSServiceError }>();

  const [value, setValue] = useState<MethodValue | undefined>();

  const defaultPayoutMethod = useDefaultPayoutMethod();
  useEffect(() => {
    if (defaultPayoutMethod) {
      setValue(defaultPayoutMethod);
    }
  }, [defaultPayoutMethod]);

  const isInternational = queryMember.data
    ? selectorUseInternationalPayouts(
        queryMember.data,
        queryCustomerEntity.data
      )
    : false;

  const showInstantPayoutMethods = isInternational
    ? queryFeatureFlags.data?.internationalIP &&
      !!queryMember.data &&
      selectorUseInternationalInstantPayouts(
        queryMember.data,
        queryCustomerEntity.data,
        queryPayoutSettings.data
      )
    : true;
  const ipFee = useInstantPayoutFee();
  const modalInfo = useModalInfo();

  const [mutate, meta] = useWSMutation<
    IPayoutSettingsResponse,
    WSServiceError,
    MethodValue
  >(
    async data => {
      if (!data.category) {
        throw new Error("No payout preference specified");
      }

      const apPayoutInternalAccount = queryInternalAccounts.data?.find(
        internalAccount =>
          selectorIsPlatformBalanceAccount(
            internalAccount,
            queryPayoutSettings.data
          )
      );

      if (
        !(
          data.category === PayoutPreferences.Standard ||
          data.category === PayoutPreferences.Instant
        )
      ) {
        throw new Error("Invalid payout preference");
      }

      if (data.category === PayoutPreferences.Instant) {
        let destinationType: PayoutDestinationType;

        if (data.type === "InternalAccount") {
          if (apPayoutInternalAccount) {
            return await paymentsService.payoutSettings.update(userId, {
              payoutPreferences: PayoutPreferences.Instant,
              instant: {
                destinationType: "InternalAccount" as PayoutDestinationType,
                destinationId: apPayoutInternalAccount.internalAccountId
              }
            });
          }

          return await paymentsService.payoutSettings.update(userId, {
            payoutPreferences: PayoutPreferences.Instant,
            instant: null
          });
        }

        switch (data.type) {
          case "Account":
            destinationType = PayoutDestinationType.Account;
            break;
          case "CheckbookCard":
            destinationType = PayoutDestinationType.Card;
            break;
          case "PaymentCard":
            destinationType = PayoutDestinationType.PaymentCard;
            break;
          default:
            throw new Error(
              `${data.type} is not a valid payout destination type for Instant payout method`
            );
        }

        return await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationType,
            destinationId: data.id
          }
        });
      }

      if (data.type === "Account") {
        return await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Standard,
          standard: {
            destinationType: PayoutDestinationType.Account,
            destinationId: data.id
          }
        });
      } else if (data.type === "InternalAccount") {
        if (apPayoutInternalAccount) {
          return await paymentsService.payoutSettings.update(userId, {
            payoutPreferences: PayoutPreferences.Standard,
            standard: {
              destinationType: "InternalAccount" as PayoutDestinationType,
              destinationId: apPayoutInternalAccount.internalAccountId
            }
          });
        }

        return await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Standard,
          standard: null
        });
      } else {
        throw new Error(
          `${data.type} is not a valid payout destination type for Standard payout method`
        );
      }
    },
    {
      onSuccess: () => {
        history.push(PATH_SETTINGS_PAYMENT_METHODS);
      },
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const modalConfirmPayoutMethodChange = useModalConfirmPayoutMethodChange();

  const isSameMethodSelected = useMemo(
    () =>
      value?.id === defaultPayoutMethod?.id &&
      value?.category === defaultPayoutMethod?.category,
    [value, defaultPayoutMethod]
  );

  return (
    <LayoutFullscreen
      headTitle="Edit payout methods"
      title="Edit payout methods"
      description="Add a new payout method or set an existing payout method as default"
      onBack={() => {
        history.push(PATH_SETTINGS_PAYMENT_METHODS);
      }}
    >
      <WSQueries
        queries={{
          queryAccounts,
          queryPayoutSettingsDebitCards,
          queryPaymentCards,
          queryInternalAccounts,
          queryPayoutSettings
        }}
      >
        {({
          queryAccounts: { data: accounts },
          queryPayoutSettingsDebitCards: { data: debitCards },
          queryPaymentCards: { data: paymentCards },
          queryInternalAccounts: { data: internalAccounts },
          queryPayoutSettings: { data: payoutSettings }
        }) => {
          return (
            <WSList gap="XL">
              {/* Display errors passed though location state */}
              <WSErrorMessage
                contextKey="PayoutSettings"
                error={location.state?.error}
              />

              <WSElement>
                <WSText weight="medium" mb="M">
                  Standard payout methods
                </WSText>

                <List
                  data={{
                    accounts: accounts.filter(selectorAccountPayoutReady),
                    internalAccounts: internalAccounts.filter(
                      internalAccount =>
                        selectorActiveBankingAccount(internalAccount) ||
                        selectorIsPlatformBalanceAccount(
                          internalAccount,
                          payoutSettings
                        )
                    )
                  }}
                  onChange={setValue}
                  value={value}
                  category={PayoutPreferences.Standard}
                  initialValue={defaultPayoutMethod}
                />

                {showInstantPayoutMethods && (
                  <>
                    <WSDivider mt="2XL" mb="3XL" />

                    <WSFlexBox gap="S" alignItems="center" mb="M">
                      <WSText weight="medium">
                        Instant payout methods ({ipFee}% fee)
                      </WSText>

                      <WSIcon
                        name="info-circle-fill"
                        color="gray500"
                        size="S"
                        onClick={() => {
                          modalInfo.open();
                        }}
                      />
                    </WSFlexBox>

                    <List
                      data={{
                        accounts: accounts.filter(account =>
                          selectorAccountIPReady(account, isInternational)
                        ),
                        checkbookCards: debitCards,
                        paymentCards: paymentCards.filter(
                          selectorPaymentCardPayoutReady
                        ),
                        internalAccounts: internalAccounts.filter(
                          internalAccount =>
                            selectorActiveBankingAccount(internalAccount) ||
                            selectorIsPlatformBalanceAccount(
                              internalAccount,
                              payoutSettings
                            )
                        )
                      }}
                      onChange={setValue}
                      value={value}
                      category={PayoutPreferences.Instant}
                      initialValue={defaultPayoutMethod}
                    />
                  </>
                )}
              </WSElement>

              <WSCard
                onClick={() => {
                  history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD);
                }}
                actions={{
                  alignment: "left",
                  buttons: [
                    {
                      icon: "plus-circle",
                      kind: "Link",
                      label: "Add new payout method"
                    }
                  ]
                }}
              />

              {/* Display mutation error close to submit action */}
              <WSErrorMessage contextKey="PayoutSettings" error={meta.error} />

              <WSActions
                alignment="fill"
                buttons={[
                  {
                    label: "Save changes",
                    onClick: () => {
                      modalConfirmPayoutMethodChange.open({
                        onSave: () => {
                          mutate(value);
                        },
                        previousMethod: defaultPayoutMethod!,
                        newMethod: value!
                      });
                    },
                    loading: meta.isLoading,
                    disabled: isSameMethodSelected
                  }
                ]}
              />
            </WSList>
          );
        }}
      </WSQueries>
    </LayoutFullscreen>
  );
};
